/** @jsx jsx */
import { Main, Container, BaseStyles, jsx } from "theme-ui"
import PropTypes from "prop-types"
import CookieConsent from "react-cookie-consent"
import { useEffect } from "react"
import TagManager from "react-gtm-module"

import SiteMetadata from "./site-metadata"
import SwitchColorMode from "./switch-color-mode"
import Header from "./header"
import Navigation from "./navigation"
import SearchModalConsumer from "./search-modal-consumer"
import Footer from "./footer"
import "../normalize.css"
import "../ie.scss"
import "../iphone.scss"

import { groupColor } from "../theme"

const Layout = ({
  children,
  logos,
  favicon,
  currentMode,
  navigation,
  currentLanguage,
  currentURL,
  defaultTitle,
  title,
  canonicalLink,
  imageURL,
  description,
  seoLinks,
}) => {
  const tagManagerArgs = {
    gtmId: "GTM-PNWXGRM",
  }

  const doAnalytics = () => {
    TagManager.initialize(tagManagerArgs)
  }

  useEffect(() => {
    if (document.cookie.indexOf("CookieConsent=true") >= 0) {
      doAnalytics()
    }
  })

  useEffect(() => {
    var iOS =
      !!navigator.platform && /iPad|iPhone|iPod/.test(navigator.platform)
    if (iOS) {
      document.body.scroll(0, 0)
    }
  })

  return (
    <BaseStyles data-cy-mode={currentMode}>
      <SiteMetadata
        title={title}
        defaultTitle={defaultTitle}
        imageURL={imageURL}
        language={currentLanguage}
        canonicalLink={canonicalLink}
        description={description}
        favicon={favicon}
      />
      <SwitchColorMode mode={currentMode} />
      <Header
        logos={logos}
        currentMode={currentMode}
        currentLanguage={currentLanguage}
        currentURL={currentURL}
        seoLinks={seoLinks}
      />
      <div sx={{ variant: "header.spacer" }}> </div>
      <Main data-mode={currentMode}>
        <Container>
          {children}
          <Footer
            language={currentLanguage}
            seoLinks={seoLinks}
            footerItems={
              navigation &&
              navigation[currentLanguage] &&
              navigation[currentLanguage].footerItems
            }
            footerSocialMediaItems={
              navigation &&
              navigation[currentLanguage] &&
              navigation[currentLanguage].footerSocialMedia
            }
          />
        </Container>
      </Main>
      <Navigation
        navMenu={navigation}
        currentMode={currentMode}
        currentLanguage={currentLanguage}
        currentURL={currentURL}
        seoLinks={seoLinks}
      />
      <SearchModalConsumer
        optionText="In diesem Bereich suchen:"
        buttonText="Suchen"
        placeholder="Suche"
        refineLanguage={[currentLanguage]}
        refineMode={
          currentMode === "default" ||
          currentMode === "group" ||
          currentMode === "contact"
            ? ["group", "food", "environment", "pharma"]
            : [currentMode]
        }
        seoLinks={seoLinks}
        appId={process.env.GATSBY_ALGOLIA_APP_ID}
        searchKey={process.env.GATSBY_ALGOLIA_SEARCH_KEY}
      />
      <div
        sx={{
          "& .cookieConsent": {
            bottom: "170px",
            transform: "translateX(-50%)",
            border: "1px solid",
            maxWidth: "800px",

            "& a": {
              color: groupColor,
              textDecoration: "none",
            },

            "& .accept-button": {
              color: "#fff !important",
              border: "1px solid !important",
              borderRadius: "20px !important",
              backgroundColor: groupColor + " !important",

              "&:hover": {
                color: groupColor + " !important",
                backgroundColor: "#fff !important",
              },
            },
            "& .decline-button": {
              variant: "styles.linkbtmbrd",
              color: groupColor + " !important",
              borderBottom: "none",
              backgroundColor: "transparent !important",
              "&:after": {
                backgroundColor: groupColor + " !important",
                content: "''",
                width: 0,
                height: 2,
                display: "block",
                transition: "200ms ease-out",
              },
            },
          },
        }}
      >
        <CookieConsent
          location="none"
          style={{ background: "#fff", color: "inherit", left: "50%" }}
          buttonClasses="accept-button"
          declineButtonClasses="decline-button"
          enableDeclineButton
          buttonText={
            navigation &&
            navigation[currentLanguage] &&
            navigation[currentLanguage].cookieAccept
          }
          declineButtonText={
            navigation &&
            navigation[currentLanguage] &&
            navigation[currentLanguage].cookieDecline
          }
          onDecline={() => {}}
          onAccept={() => {
            doAnalytics()
          }}
        >
          {navigation &&
            navigation[currentLanguage] &&
            navigation[currentLanguage].cookieText}
        </CookieConsent>
      </div>
    </BaseStyles>
  )
}

Layout.propTypes = {
  children: PropTypes.node.isRequired,
  logos: PropTypes.object.isRequired,
  favicon: PropTypes.string.isRequired,
  currentMode: PropTypes.oneOf([
    "default",
    "group",
    "pharma",
    "food",
    "environment",
    "contact",
  ]).isRequired,
  navigation: PropTypes.object.isRequired,
  seoLinks: PropTypes.object.isRequired,
  currentLanguage: PropTypes.oneOf(["de", "en"]).isRequired,
  currentURL: PropTypes.string.isRequired,
}

Layout.defaultProps = {
  currentMode: "default",
  navigation: {},
  seoLinks: {},
  currentLanguage: "de",
}

export default Layout
